import React, { useState } from 'react'
import { Link, useLocation, Routes, Route, Navigate } from 'react-router-dom'
import {
  Home,
  Settings,
  BarChart2,
  Brain,
  User,
  Menu,
  X,
  LogOut,
  Shield,
  Plus
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'

// Pages
import Dashboard from '@/pages/Dashboard'
import SimpleDashboard from '@/pages/SimpleDashboard'
import Assets from '@/pages/Assets'
import Analytics from '@/pages/Analytics'
import Rules from '@/pages/Rules'
import Profile from '@/pages/Profile'
import SettingsPage from '@/pages/Settings'
import Campaigns from '@/pages/Campaigns'
import CampaignNew from '@/pages/CampaignNew'
import MinimalCampaignBuilder from '@/pages/MinimalCampaignBuilder'
import CampaignConfiguration from '@/pages/CampaignConfiguration'
import TraffickingPreview from '@/pages/TraffickingPreview'

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Campaigns', href: '/campaigns', icon: Brain },
  { name: 'Configuration', href: '/rules', icon: Shield },
]

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(true) // Always start minified
  const location = useLocation()
  const { user, logout } = useAuth()

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="flex h-screen bg-white dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 dark:bg-black/70 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        ${collapsed ? 'w-20' : 'w-64'} bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 ease-in-out
        fixed inset-y-0 left-0 z-50 transform lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-800">
          <div className={`flex items-center ${collapsed ? 'justify-center w-full' : 'space-x-3'}`}>
            {collapsed ? (
              <img 
                src="/favicon-dataraiils.png" 
                alt="DataRaiils" 
                className="h-10 w-10"
              />
            ) : (
              <>
                <img 
                  src="/dataraiils-logo.png" 
                  alt="DataRaiils" 
                  className="h-8 w-auto flex-shrink-0 dark:hidden"
                />
                <img 
                  src="/dataraiils-logo-DR.png" 
                  alt="DataRaiils" 
                  className="h-8 w-auto flex-shrink-0 hidden dark:block"
                />
                <span className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white">DataRaiils</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="hidden lg:inline-flex text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setCollapsed(c => !c)}
              aria-label="Toggle sidebar"
            >
              {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-4'} py-6 space-y-2`}>
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center ${collapsed ? 'justify-center p-3' : 'px-3 py-2.5'} text-sm font-medium rounded-xl transition-all duration-200 group relative
                  ${isActive 
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 shadow-sm' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
                onClick={() => setSidebarOpen(false)}
                title={collapsed ? item.name : ''}
              >
                <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} ${isActive ? 'text-blue-600' : ''}`} />
                {!collapsed && <span>{item.name}</span>}
                {/* Tooltip for minified state */}
                {collapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {item.name}
                  </div>
                )}
              </Link>
            )
          })}
          
          {/* Create Campaign Button */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
            <Link
              to="/campaigns/new"
              className={`
                flex items-center ${collapsed ? 'justify-center p-3' : 'px-3 py-2.5'} text-sm font-medium rounded-xl transition-all duration-200 group relative
                bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl
              `}
              onClick={() => setSidebarOpen(false)}
              title={collapsed ? 'Create Campaign' : ''}
            >
              <Plus className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
              {!collapsed && <span>Create Campaign</span>}
              {/* Tooltip for minified state */}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  Create Campaign
                </div>
              )}
            </Link>
          </div>
        </nav>

        {/* User section */}
        <div className="border-t border-gray-100 dark:border-gray-800 p-4">
          <Link
            to="/settings"
            className={`flex items-center ${collapsed ? 'justify-center mb-2' : 'space-x-3 mb-3'} p-2 rounded-xl bg-gray-50 dark:bg-gray-800 group relative hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer`}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {user?.firstName || user?.email
                ? (user?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase()
                : 'U'
              }
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user?.firstName || user?.email?.split('@')[0] || 'User'
                  }
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || 'user@dataraiils.com'}
                </p>
              </div>
            )}
            {/* Tooltip for minified state */}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {user?.firstName && user?.lastName 
                  ? `${user.firstName} ${user.lastName}`
                  : user?.firstName || user?.email?.split('@')[0] || 'User'
                } - Settings
              </div>
            )}
          </Link>
          
          <button
            onClick={handleLogout}
            className={`
              flex items-center w-full ${collapsed ? 'justify-center p-2 mt-2' : 'px-3 py-2 mt-1'} text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors group relative
            `}
            title={collapsed ? 'Sign Out' : ''}
          >
            <LogOut className={`w-4 h-4 ${collapsed ? '' : 'mr-3'}`} />
            {!collapsed && <span>Sign Out</span>}
            {/* Tooltip for minified state */}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Sign Out
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between h-16 px-6">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            {/* Mini brand for collapsed/mobile */}
            <div className="flex items-center gap-2 lg:hidden">
              <img src="/dataraiils-logo.png" alt="DataRaiils" className="h-6 w-auto dark:hidden" />
              <img src="/dataraiils-logo-DR.png" alt="DataRaiils" className="h-6 w-auto hidden dark:block" />
              <span className="text-sm font-semibold text-gray-900 dark:text-white">DataRaiils</span>
            </div>

            <div className="flex-1" />
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
          <Routes>
            <Route path="/" element={<SimpleDashboard />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/campaigns/new" element={<CampaignNew />} />
            <Route path="/campaigns/:id" element={<MinimalCampaignBuilder />} />
            <Route path="/campaign-builder" element={<Navigate to="/campaigns" replace />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/trafficking/preview" element={<TraffickingPreview />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}